import { useMemo, useCallback } from 'react';

import type { MessageDescriptor } from 'react-intl';
import { useDispatch } from 'react-redux';

import { AppearanceTypes } from '@atlaskit/flag';

import { showFlag, showFlagComponent, dismissFlag } from 'src/redux/flags';
import {
  ComponentFlagId,
  FlagText,
  SimpleFlagProps,
} from 'src/redux/flags/types';
import { isPdvBot } from 'src/utils/pdvs';

import { messages } from './flag.i18n';

type PredefinedFlagText = FlagText | MessageDescriptor;

const extractTextContent = (description: PredefinedFlagText): FlagText => {
  if (typeof description === 'string' || 'msg' in description) {
    return description;
  }
  return {
    msg: description,
  };
};

/**
 *  Show a predefined flag with the given type, description, title, and coloredAppearance
 * @param type - The type of flag: error | info | normal | success | warning
 * @param description - The message in the body of the flag
 * @param title - [optional] The title at the top of the flag
 * @param coloredAppearance - [optional] Whether the flag should have a colored appearance based on its' type
 * @param id - [optional] The ID used for the flag, otherwise generic ID will be created. Eventually we should make
 * this required
 */
const showPredefinedFlag = (
  type: AppearanceTypes,
  description: PredefinedFlagText,
  title?: PredefinedFlagText,
  coloredAppearance?: boolean,
  id?: string,
  sentryErrorProps?: { sentryId: string; includeOrigDesc?: boolean }
) =>
  showFlag({
    id: id || `flags-hook-${type}`,
    title: title ? extractTextContent(title) : { msg: messages[type] },
    description: extractTextContent(description),
    iconType: type,
    sentryErrorProps,
    coloredAppearance,
    autoDismiss: true,
  });

export const useFlag = ({
  enableForPdvs = false,
}: { enableForPdvs?: boolean } = {}) => {
  const dispatch = useDispatch();

  const dispatchWrapper = useCallback(
    <T extends any[]>(fn: (...args: T) => any) =>
      (...args: T) => {
        if (!enableForPdvs && isPdvBot()) return;
        dispatch(fn(...args));
      },
    [dispatch, enableForPdvs]
  );

  const flagActions = useMemo(() => {
    return {
      dismissFlag: dispatchWrapper((flagId: ComponentFlagId | string) =>
        dismissFlag(flagId)
      ),
      showFlag: dispatchWrapper(
        (flag: Omit<SimpleFlagProps, 'handleDismissed'>) => showFlag(flag)
      ),
      showFlagComponent: dispatchWrapper((id: ComponentFlagId | string) =>
        showFlagComponent(id)
      ),
      showErrorFlag: dispatchWrapper(
        (
          description: PredefinedFlagText,
          title?: PredefinedFlagText,
          coloredAppearance?: boolean,
          id?: string,
          sentryErrorProps?: { sentryId: string; includeOrigDesc?: boolean }
        ) =>
          showPredefinedFlag(
            'error',
            description,
            title,
            coloredAppearance,
            id,
            sentryErrorProps
          )
      ),
      showInfoFlag: dispatchWrapper(
        (
          description: PredefinedFlagText,
          title?: PredefinedFlagText,
          coloredAppearance?: boolean,
          id?: string
        ) =>
          showPredefinedFlag('info', description, title, coloredAppearance, id)
      ),
      showNormalFlag: dispatchWrapper(
        (
          description: PredefinedFlagText,
          title?: PredefinedFlagText,
          coloredAppearance?: boolean,
          id?: string
        ) =>
          showPredefinedFlag(
            'normal',
            description,
            title,
            coloredAppearance,
            id
          )
      ),
      showSuccessFlag: dispatchWrapper(
        (
          description: PredefinedFlagText,
          title?: PredefinedFlagText,
          coloredAppearance?: boolean,
          id?: string
        ) =>
          showPredefinedFlag(
            'success',
            description,
            title,
            coloredAppearance,
            id
          )
      ),
      showWarningFlag: dispatchWrapper(
        (
          description: PredefinedFlagText,
          title?: PredefinedFlagText,
          coloredAppearance?: boolean,
          id?: string
        ) =>
          showPredefinedFlag(
            'warning',
            description,
            title,
            coloredAppearance,
            id
          )
      ),
    };
  }, [dispatchWrapper]);

  return flagActions;
};
